const STORAGE_KEY = 'demo:appearance';

export const APPEARANCE_CHANGED_EVENT = 'demo:appearance:changed';

export type ColorScheme = 'light' | 'dark' | 'system';

export interface HeaderButtons {
  translation: boolean;
  activity: boolean;
  notifications: boolean;
  search: boolean;
  userMenu: boolean;
  pageActions: boolean;
}

export interface AppearanceStore {
  colorScheme?: ColorScheme;
  themeName?: string;
  topbarColor?: string;
  shellColor?: string;
  accentColor?: string;
  headerButtons?: Partial<HeaderButtons>;
}

const DEFAULT_HEADER_BUTTONS: HeaderButtons = {
  translation: true,
  activity: true,
  notifications: true,
  search: true,
  userMenu: true,
  pageActions: true,
};

export function loadAppearance(): AppearanceStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AppearanceStore) : {};
  } catch {
    return {};
  }
}

export function getHeaderButtons(): HeaderButtons {
  const stored = loadAppearance();
  return { ...DEFAULT_HEADER_BUTTONS, ...(stored.headerButtons ?? {}) };
}

export function saveAppearance(next: AppearanceStore): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  window.dispatchEvent(new Event(APPEARANCE_CHANGED_EVENT));
}

function resolveToHex(cssExpression: string): string | null {
  if (typeof document === 'undefined' || !document.body) return null;
  // Give the element real dimensions and keep it in the render tree so the
  // browser always computes the background-color (opacity:0 on a 0x0 fixed
  // element can be skipped by some engines).
  const el = document.createElement('div');
  el.style.cssText =
    `background:${cssExpression};width:1px;height:1px;` +
    `position:fixed;top:0;left:0;pointer-events:none;z-index:-1;`;
  document.body.appendChild(el);
  const raw = getComputedStyle(el).backgroundColor;
  document.body.removeChild(el);

  // Fast path: rgb(r, g, b) or rgba(r, g, b, a) — always integers 0-255
  const rgbMatch = raw.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    return (
      '#' +
      rgbMatch
        .slice(1, 4)
        .map((n) => Number(n).toString(16).padStart(2, '0'))
        .join('')
    );
  }

  // Fallback: browser returned oklch/color() — paint to canvas to get sRGB bytes
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = raw;
    ctx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    if (a === 0) return null; // transparent — variable unresolved
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return null;
  }
}

export function syncThemeColor(): void {
  // Double-RAF: first frame commits DOM attribute changes, second frame
  // guarantees the cascade has been fully recalculated before we read styles.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const color = resolveToHex('var(--color-topbar,var(--primary))');
      if (!color) return;
      let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', color);
    });
  });
}

export function applyScheme(scheme: ColorScheme): void {
  const root = document.documentElement;
  if (scheme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.setAttribute('data-mode', prefersDark ? 'dark' : 'light');
  } else {
    root.setAttribute('data-mode', scheme);
  }
  syncThemeColor();
}

export function applyTheme(themeName: string): void {
  const root = document.documentElement;
  const resolved = themeName === '__default__' ? '' : themeName;
  if (resolved) root.setAttribute('data-theme', resolved);
  else root.removeAttribute('data-theme');
  syncThemeColor();
}

export function applyAccent(accentColor: string): void {
  const root = document.documentElement;
  if (accentColor) {
    root.style.setProperty('--primary', accentColor);
    root.style.setProperty('--color-primary', accentColor);
  } else {
    root.style.removeProperty('--primary');
    root.style.removeProperty('--color-primary');
  }
  syncThemeColor();
}

export function applyColors(topbarColor: string, shellColor: string): void {
  const root = document.documentElement;
  if (topbarColor) root.style.setProperty('--color-topbar', topbarColor);
  else root.style.removeProperty('--color-topbar');
  if (shellColor) root.style.setProperty('--color-shell', shellColor);
  else root.style.removeProperty('--color-shell');
  syncThemeColor();
}
